import { alpha, Box } from '@mui/material';
import { ItemTagIconKey } from '../../../../app/services/api/itemTags';
import {
  DEFAULT_ITEM_TAG_COLORS,
  isValidHexColor,
  normalizeHexColor,
  resolveMaterialIcon,
} from './itemTagUtils';

export type ItemTagChipProps = {
  name: string;
  textColor: string;
  backgroundColor: string;
  iconKey: ItemTagIconKey;
  isActive: boolean;
  placeholderLabel: string;
  /**
   * Scales the whole chip visually while keeping the same layout.
   * Example: 1 = normal, 0.8 = smaller, 0.65 = very small.
   */
  scale?: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const hexToHsl = (hex: string) => {
  let c = hex.replace(/^#/, '');

  if (c.length === 3) {
    c = c
      .split('')
      .map((char) => char + char)
      .join('');
  }

  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;

    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }

    h /= 6;
  }

  return {
    h: h * 360,
    s: s * 100,
    l: l * 100,
  };
};

const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const a = s * Math.min(l, 1 - l);

  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };

  return `#${f(0)}${f(8)}${f(4)}`;
};

const isLightColor = (color: string) => hexToHsl(color).l > 82;

const getUsableChipBaseColor = (baseColor: string) => {
  const hsl = hexToHsl(baseColor);

  if (hsl.l < 45) {
    return hslToHex(hsl.h, clamp(hsl.s + 8, 42, 78), 76);
  }

  if (hsl.l > 90) {
    return hslToHex(hsl.h, clamp(hsl.s + 12, 32, 72), 82);
  }

  return baseColor;
};

const getChipTextColor = (textColor: string, originalBackgroundColor: string) => {
  const backgroundHsl = hexToHsl(originalBackgroundColor);

  if (backgroundHsl.l < 45 && isLightColor(textColor)) {
    return originalBackgroundColor;
  }

  return textColor;
};

const getIconPunchHue = (hue: number) => {
  if (hue >= 330 || hue <= 25) {
    return (hue + 30) % 360;
  }

  if (hue > 25 && hue <= 65) {
    return (hue + 12) % 360;
  }

  if (hue > 65 && hue <= 165) {
    return (hue - 42 + 360) % 360;
  }

  if (hue > 165 && hue <= 245) {
    return (hue - 22 + 360) % 360;
  }

  return (hue + 24) % 360;
};

const getTransitionHue = (baseHue: number, punchHue: number) => {
  const diff = ((punchHue - baseHue + 540) % 360) - 180;

  return (baseHue + diff * 0.45 + 360) % 360;
};

const getChipPalette = (baseColor: string) => {
  const hsl = hexToHsl(baseColor);

  const isNeutral = hsl.s < 22;
  const isVeryLight = hsl.l > 76;
  const isGreen = hsl.h > 65 && hsl.h <= 165;

  const mainColor = hslToHex(
    hsl.h,
    clamp(hsl.s, 0, 100),
    clamp(hsl.l, 38, 88),
  );

  let iconPunchColor: string;
  let iconTransitionColor: string;
  let mainLeftColor: string;

  if (isGreen) {
    iconPunchColor = hslToHex(
      (hsl.h - 42 + 360) % 360,
      clamp(hsl.s + 42, 70, 100),
      clamp(hsl.l + 5, 66, 86),
    );

    iconTransitionColor = hslToHex(
      (hsl.h - 24 + 360) % 360,
      clamp(hsl.s + 26, 58, 100),
      clamp(hsl.l + 3, 62, 84),
    );

    mainLeftColor = hslToHex(
      (hsl.h - 18 + 360) % 360,
      clamp(hsl.s + 16, 48, 100),
      clamp(hsl.l + 2, 58, 82),
    );
  } else {
    const punchHue = getIconPunchHue(hsl.h);
    const transitionHue = getTransitionHue(hsl.h, punchHue);

    iconPunchColor = hslToHex(
      punchHue,
      isNeutral ? clamp(hsl.s + 48, 42, 86) : clamp(hsl.s + 34, 0, 100),
      isVeryLight ? clamp(hsl.l - 12, 48, 74) : clamp(hsl.l + 10, 56, 94),
    );

    iconTransitionColor = hslToHex(
      transitionHue,
      isNeutral ? clamp(hsl.s + 34, 34, 76) : clamp(hsl.s + 18, 0, 100),
      isVeryLight ? clamp(hsl.l - 6, 50, 80) : clamp(hsl.l + 5, 50, 91),
    );

    mainLeftColor = hslToHex(
      hsl.h,
      isNeutral ? clamp(hsl.s + 14, 20, 58) : clamp(hsl.s + 6, 0, 100),
      clamp(hsl.l + 1, 42, 88),
    );
  }

  const rightFadeColor = hslToHex(
    hsl.h,
    clamp(hsl.s - 10, 0, 100),
    clamp(hsl.l + 8, 50, 95),
  );

  return {
    iconPunchColor,
    iconTransitionColor,
    mainLeftColor,
    mainColor,
    rightFadeColor,
  };
};

const getPaletteChipGradient = (baseColor: string) => {
  const hsl = hexToHsl(baseColor);

  const isGreen = hsl.h > 65 && hsl.h <= 165;
  const isBlue = hsl.h > 165 && hsl.h <= 245;
  const isRedOrPink = hsl.h >= 330 || hsl.h <= 25;
  const isNeutral = hsl.s < 22;

  const {
    iconPunchColor,
    iconTransitionColor,
    mainLeftColor,
    mainColor,
    rightFadeColor,
  } = getChipPalette(baseColor);

  if (isGreen) {
    return `
      radial-gradient(
        ellipse 42px 32px at 14px 50%,
        ${alpha(iconPunchColor, 0.70)} 0%,
        ${alpha(iconPunchColor, 0.62)} 32%,
        ${alpha(iconTransitionColor, 0.28)} 58%,
        ${alpha(iconTransitionColor, 0)} 82%
      ),
      linear-gradient(
        90deg,
        ${iconPunchColor} 0%,
        ${mainLeftColor} 18%,
        ${mainColor} 52%,
        ${rightFadeColor} 100%
      )
    `;
  }

  if (isBlue) {
    return `
      radial-gradient(
        ellipse 42px 32px at 14px 50%,
        ${alpha(iconPunchColor, 0.50)} 0%,
        ${alpha(iconPunchColor, 0.34)} 32%,
        ${alpha(iconTransitionColor, 0.18)} 58%,
        ${alpha(iconTransitionColor, 0)} 82%
      ),
      linear-gradient(
        90deg,
        ${iconTransitionColor} 0%,
        ${mainLeftColor} 18%,
        ${mainColor} 52%,
        ${rightFadeColor} 100%
      )
    `;
  }

  if (isRedOrPink) {
    return `
      radial-gradient(
        ellipse 44px 32px at 14px 50%,
        ${alpha(iconPunchColor, 0.52)} 0%,
        ${alpha(iconPunchColor, 0.36)} 32%,
        ${alpha(iconTransitionColor, 0.20)} 58%,
        ${alpha(iconTransitionColor, 0)} 82%
      ),
      linear-gradient(
        90deg,
        ${iconTransitionColor} 0%,
        ${mainLeftColor} 16%,
        ${mainColor} 52%,
        ${rightFadeColor} 100%
      )
    `;
  }

  if (isNeutral) {
    return `
      radial-gradient(
        ellipse 44px 32px at 14px 50%,
        ${alpha(iconPunchColor, 0.58)} 0%,
        ${alpha(iconPunchColor, 0.40)} 32%,
        ${alpha(iconTransitionColor, 0.22)} 58%,
        ${alpha(iconTransitionColor, 0)} 82%
      ),
      linear-gradient(
        90deg,
        ${iconTransitionColor} 0%,
        ${mainLeftColor} 16%,
        ${mainColor} 52%,
        ${rightFadeColor} 100%
      )
    `;
  }

  return `
    radial-gradient(
      ellipse 44px 32px at 14px 50%,
      ${alpha(iconPunchColor, 0.54)} 0%,
      ${alpha(iconPunchColor, 0.36)} 32%,
      ${alpha(iconTransitionColor, 0.20)} 58%,
      ${alpha(iconTransitionColor, 0)} 82%
    ),
    linear-gradient(
      90deg,
      ${iconTransitionColor} 0%,
      ${mainLeftColor} 16%,
      ${mainColor} 52%,
      ${rightFadeColor} 100%
    )
  `;
};

/**
 * Renders a single item tag as a styled pill/chip.
 * Use this component anywhere a tag needs to be displayed:
 * storefront product rows etc.
 */
export const ItemTagChip = ({
  name,
  textColor,
  backgroundColor,
  iconKey,
  isActive,
  placeholderLabel,
  scale = 1,
}: ItemTagChipProps) => {
  const IconComponent = resolveMaterialIcon(iconKey);

  const resolvedTextColor = isValidHexColor(textColor)
    ? normalizeHexColor(textColor)
    : DEFAULT_ITEM_TAG_COLORS.textColor;

  const resolvedBackgroundColor = isValidHexColor(backgroundColor)
    ? normalizeHexColor(backgroundColor)
    : DEFAULT_ITEM_TAG_COLORS.backgroundColor;

  const chipBaseColor = getUsableChipBaseColor(resolvedBackgroundColor);
  const chipTextColor = getChipTextColor(
    resolvedTextColor,
    resolvedBackgroundColor,
  );

  const label = name || placeholderLabel;

  const chipWidth = 110;
  const chipHeight = 32;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          alignSelf: 'flex-start',
          minWidth: chipWidth,
          minHeight: chipHeight,
          pl: '5px',
          pr: '8px',
          py: 0.48,
          gap: '2px',
          borderRadius: '999px',
          overflow: 'hidden',
          backgroundColor: chipBaseColor,
          backgroundImage: getPaletteChipGradient(chipBaseColor),
          color: chipTextColor,
          border: 'none',
          boxShadow: `0 1px 2px ${alpha('#101828', 0.02)}`,
          opacity: isActive ? 1 : 0.55,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 22,
            height: 30,
            color: chipTextColor,
          }}
        >
          <IconComponent
            sx={{
              display: 'block',
              fontSize: 20,
              transform: 'translateY(-1px)',
            }}
          />
        </Box>

        <Box
          component="span"
          title={label}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'block',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            color: chipTextColor,
            WebkitTextFillColor: chipTextColor,
            fontSize: 12,
            fontWeight: 650,
            letterSpacing: 0,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Box>
      </Box>
    </Box>
  );
};